/**
 * The written story of Survivor — "The Unmade Gauntlet". Pure data; the
 * narrative lives here and nowhere else. Read by ui/LoreMenu.js (the Archive
 * codex), Game._showBossTitle (the one-line boss `epithet` shown at each
 * fight start), and RunEndOverlay (the EPILOGUE on a full clear).
 *
 * BOSS_LORE is keyed by boss `spriteKey` (see data/bosses.js + challengeBosses
 * .js) and CHAMPION_LORE by character `id` (data/characters.js), so boss/
 * champion names and mechanics stay the single source of truth — every entry
 * below deepens an identity the fight already expresses. Tone is mythic and
 * elegiac: a kingdom of sound, unmade. (lore.test.js asserts no roster entry
 * is missing an entry and no entry is an orphan.)
 */

export const PROLOGUE =
  'Melodia was a kingdom kept in tune — every stone, every hour, every living '
  + 'thing held in place by one long chord struck at its heart, and by the crown '
  + 'that struck it. No one remembers the note that broke. Only that the King who '
  + 'held it came undone, and began, patiently, to unmake his own realm: erasing '
  + 'the ground beneath it, stilling its clocks, unwriting what had been written. '
  + 'The court did not flee. It could not — it was made of the same failing music. '
  + 'So it curdled in place, each lord and officer clenched around the one duty '
  + 'that had defined them, playing it now to ruin.\n\n'
  + 'You are what the Unmaking left standing. Climb the dying court. Reach the '
  + 'throne at the end of the world. Unmake the one who is unmaking everything.';

export const EPILOGUE =
  'The last cell of the floor winks out, and the King with it — and for one held '
  + 'breath there is nothing at all: no court, no crown, no chord. Then you are '
  + 'still standing, which the King swore was impossible.\n\n'
  + 'Whether the note begins again behind you, whether Melodia rebuilds from the '
  + 'one survivor who would not be unmade, or whether you have merely finished the '
  + "King's work and unmade the Unmaker too — the silence does not say. It only "
  + 'makes room. You walk into it. Somewhere, very faint, something is almost a song.';

// Act II opening — the door the Act I EPILOGUE left ajar, read from the other
// side. Surfaced in the Archive (ui/LoreMenu.js) once the King has fallen, and
// shown at the top of an Act II run (ACT_OPENINGS below).
export const PROLOGUE_II =
  'The note did begin again. That was the mistake — to think a kingdom of sound '
  + 'could end in silence, when silence was only ever the thing sound was laid '
  + 'over. Melodia\'s chord did not fill an empty world. It covered one.\n\n'
  + 'Beneath the tuned stones, beneath the counted hours, beneath the throne and '
  + 'the court and the crown that struck the note, there was the deep, which had '
  + 'never been sung to and did not want to be. The chord kept it down the way a '
  + 'hand keeps a held breath. When the King came undone he let the breath go.\n\n'
  + 'Now the ground you saved is a lid, and something underneath it is awake and '
  + 'rising, tuneless and patient and very old. The court you climbed was Melodia '
  + 'at its most human — proud, grieving, clenched around its little offices. What '
  + 'comes now was here before any of that, and will be here after.\n\n'
  + 'You would not be unmade. So you go down to meet the thing that was never made '
  + 'at all. Descend the Undersong. There is no throne at the bottom. There is only '
  + 'the first silence, and whatever has learned to move through it.';

// Act II ending — shown by RunEndOverlay when the Undersong falls. The Act I
// EPILOGUE above is Act I's own finish again, now that the King ends that act
// rather than opening a trapdoor into this one (data/acts.js).
export const EPILOGUE_II =
  'The Undersong comes apart the way a held breath finally leaves — not a death, '
  + 'because it was never quite alive, but a settling. The dark thins. The weight '
  + 'lifts off your shots. The two hating voices fall, at last, into one.\n\n'
  + 'Melodia is not saved; there is no one left to save it, and nothing to save it '
  + 'from now but the plain quiet it was always sung over. What you have done is '
  + 'smaller and stranger than a rescue: you went down into the thing beneath the '
  + 'song and taught it, note by note, that it could be answered. Whatever begins '
  + 'again up there begins knowing that. You climb back toward the light you '
  + 'carried, humming something that is, for the first time, your own.';

// Act III opening — the door the Act II EPILOGUE left ajar, read from the other
// side. Surfaced in the Archive (ui/LoreMenu.js) and shown at the top of an
// Act III run, exactly like Act II's: every act is its own campaign now
// (ACT3-BIBLE §1, and data/acts.js for the split that made Act II one too).
export const PROLOGUE_III =
  'You climbed out humming. That was all it was — a few notes, badly kept, the '
  + 'kind of thing a person makes to prove to themselves that they are still '
  + 'making anything. You did not write it down. You did not mean it as a '
  + 'world.\n\n'
  + 'But Melodia had been emptied of its chord and taught, note by note, that it '
  + 'could be answered — and an emptied world takes the first thing offered and '
  + 'builds on it. Something heard you. Then something heard that. By the time '
  + 'you noticed, your phrase had verses you had never sung, in keys you cannot '
  + 'reach, carried by voices with no idea a person started it.\n\n'
  + 'This is not the crown’s song, which was law. It is not the deep’s '
  + 'silence, which was older than law. It is yours, gone feral: every habit you '
  + 'have, repeated back at you by a world that learned them by watching. The '
  + 'ground remembers where you walk. The air remembers what you carry. Whatever '
  + 'comes to meet you at the end of it will be wearing your own phrasing.\n\n'
  + 'A song does not need its author. That is the good news, and it is the only '
  + 'bad news there is. Go and hear what you started.';

// Act III ending — shown by RunEndOverlay when the Anthem falls. The act asked
// whether the survivor takes the song back, lets it go, or admits there was
// never a difference; the finale's own mechanics answer it, so this does too.
export const EPILOGUE_III =
  'The Anthem does not die so much as finish. The last chord holds, and holds, '
  + 'and then simply is not being held any more, and the daylight it was made of '
  + 'goes on standing there without it.\n\n'
  + 'You could take it back. That was always the offer, and it is a real one: '
  + 'name it, date it, put your hand on it, and every voice out there carrying it '
  + 'becomes a voice carrying something of yours. You have earned that the way '
  + 'nobody has earned anything in this kingdom for a long time.\n\n'
  + 'You do not. Not out of grace — you are too tired for grace — but because '
  + 'somewhere in the middle of being shot at by your own footprints, by your own '
  + 'guns, by a second body wearing your own hands, the question stopped meaning '
  + 'anything. A song you can still be killed by is not a song you own. It was '
  + 'never the difference between keeping it and letting it go. It was only ever '
  + 'the difference between a thing that needs you and a thing that does not, and '
  + 'you have just spent an act finding out which one you made.\n\n'
  + 'So you leave it where it is: enormous, borrowed, wrong in a hundred mouths, '
  + 'entirely alive. Somewhere below all that noise a person is walking out of an '
  + 'arena, humming something new and small and unrecorded. It is a terrible '
  + 'habit. She has no intention of stopping.';

/**
 * The ending each act earns, keyed by act id (data/acts.js) — drawn by
 * ui/RunEndOverlay on a win.
 *
 * Act I has one again. EPILOGUE was written as the game's ending, then spent a
 * version as the descent interstitial's copy — a threshold rather than a finish
 * — because the King's fall dropped the run straight into Act II. Now that
 * Act I ends on him it reads as what it always was.
 */
export const ACT_EPILOGUES = {
  act1: EPILOGUE,
  act2: EPILOGUE_II,
  act3: EPILOGUE_III,
};

/**
 * The screen an act opens on, keyed by act id (data/acts.js) — emitted as
 * `act:opening` by Game._startStage and drawn by ui/ActOpeningOverlay.
 *
 * Act I has no entry on purpose: it starts on the first wave with no preamble,
 * because it is where a new player lands and PROLOGUE is already the game's
 * front matter. Acts II and III are chosen from the menu, so without this their
 * prologues would only ever be read in the Archive. Act II's used to arrive
 * mid-run at the King's fall instead; that doorway closed when the acts split.
 */
export const ACT_OPENINGS = {
  act2: {
    tagline: 'The gauntlet is cleared. The floor becomes a lid.',
    prose: PROLOGUE_II,
    name: 'THE UNDERSONG',
    action: 'Descend',
  },
  act3: {
    tagline: 'You climbed out of the dark humming. Something was listening.',
    prose: PROLOGUE_III,
    name: 'THE ANTHEM',
    action: 'Go and hear it',
  },
};

// Keyed by boss spriteKey. `epithet` = one line under the boss title in-play;
// `entry` = the Archive codex paragraph.
export const BOSS_LORE = {
  // --- The Court: the seven-boss gauntlet, in order ---
  hollowSovereign: {
    epithet: 'The first crown, holding court over no one.',
    entry:
      'It ruled before the others, and rules still, though there is nothing left '
      + 'to rule. An empty circlet on an empty seat, it keeps the old rites out of '
      + 'habit — summoning petitioners who no longer exist, moving them about the '
      + 'floor as if the court still turned. Step where it commands and you become '
      + 'the last of its subjects. It does not hate you. It has simply forgotten the '
      + 'difference between a guest and a ghost.',
  },
  ironCadence: {
    epithet: 'The realm’s timekeeper, gone tyrant of the beat.',
    entry:
      'Every kingdom of sound needs a hand to keep its measure. The Cadence was '
      + 'that hand — the conductor who set the pace of harvests, of bells, of breath. '
      + 'When the chord broke it did not lose the beat; it lost mercy. Now it '
      + 'quantizes the world to a merciless meter, and everything obeys: bullets '
      + 'lurch and freeze in lockstep, its baton falls only on the grid it has '
      + 'already drawn, and when it calls the Grand Pause even the air holds still — '
      + 'before it all resumes at once, and faster. Dodge on the rests. Commit on the '
      + 'downbeat. There is no other tempo left.',
  },
  rivenChoir: {
    epithet: 'The voice that sang the realm into being, split against itself.',
    entry:
      'In the beginning the Choir sang, and Melodia was — one voice in many '
      + 'throats, perfectly aligned. The Unmaking did not silence it. It split it, so '
      + 'that every throat now sings a fraction behind the last, each certain it '
      + 'carries the true melody. The arena becomes their sheet music; the only safe '
      + 'ground is the resting staves, the silence between their lines. To survive the '
      + 'Choir is to find the one place their disagreement leaves untouched.',
  },
  famishedCourt: {
    epithet: 'Courtiers who feasted while the realm starved.',
    entry:
      'They dined through the kingdom’s long hunger and never noticed the tables '
      + 'thinning. Now the hunger is all that remains of them. The Court eats without '
      + 'end — sending its own courtiers marching to the maw, growing fat and fierce '
      + 'on each one it swallows. Deny it its meal, or drown in what it becomes. But a '
      + 'glutton overreaches: gorge it past its fill and, for a moment, it is too '
      + 'heavy to defend the crown it guards.',
  },
  ashenThrone: {
    epithet: 'The seat of power, burned; the ground remembers.',
    entry:
      'Not a person — a place. The throne itself, and the scorched hall around it, '
      + 'which took the fire that ended the old reign and never let it go. The ground '
      + 'here holds a grudge against footsteps: walk, and your own trail smoulders '
      + 'behind you, then catches. The Ashen Throne asks one question of everyone who '
      + 'approaches, the same question thrones always ask. Can you stand still enough '
      + 'to be worthy — or will you keep running, and burn the path you came by?',
  },
  jealousRegent: {
    epithet: 'The consort passed over for the crown, who cannot let you leave.',
    entry:
      'It stood beside the throne for a hundred years and was never offered it. '
      + 'When the King unmade himself, the Regent’s grief curdled into something '
      + 'with reach. It cannot bear distance — not from the crown it was denied, not '
      + 'from you. Stray too far and it drags you back into its devotion; walls you in '
      + 'with turning blades; binds you to a spot and calls it a vow. The whole world '
      + 'has taught you to keep your distance. The Regent will teach you what that '
      + 'costs.',
  },
  unmadeKing: {
    epithet: 'The wound at the heart of the world, unmaking it still.',
    entry:
      'There is no describing what he was; the Unmaking took even that. What '
      + 'remains is a shape that deletes — the floor beneath you blinks out of '
      + 'existence at his word, and where he walks the world simply stops being. Press '
      + 'him and he ascends into a truer, worse form; press that, and he begins the '
      + 'last work: the Unmaking drawn inward, closing on the single point where you '
      + 'still stand. He is not defending Melodia. He is finishing it. You are the '
      + 'only thing he has not yet erased.',
  },

  // --- The Heralds: the four minibosses before the King (shared entry) ---
  heralds: {
    epithet: 'The founding court, reforged as the King’s honor guard.',
    entry:
      'Before the throne at the end, four remnants of the old court stand watch — '
      + 'the Sovereign’s Herald, the Choir Fragment, the Throne Vanguard, the '
      + 'King’s Harbinger. Each is a piece of a fallen lord, hammered thin and set '
      + 'to a single duty: let nothing reach the King unproven. They are lesser than '
      + 'what they were. That is the point. The King keeps them close not for their '
      + 'strength, but because they were the last to love him.',
  },

  // Act II's answering guard set (data/act2Bosses.js ACT2_MINIBOSSES) — the
  // deep's version of the same idea, and pointedly not the same thing at all.
  deepWardens: {
    epithet: 'Leavings of the deep, standing where a guard would stand.',
    entry:
      'Three shapes wait on the last stretch down — a scrap of the dark, an echo '
      + 'with only half its quarrel left, a shard still heavy enough to bend what '
      + 'passes it. Call them wardens if you like. The word is yours, not theirs. '
      + 'The King’s heralds guarded him because they had loved him; these were '
      + 'never given anything to love, or any orders, or any name. They are simply '
      + 'what the Undersong has already finished being, left lying in the way. That '
      + 'you must still fight through them says nothing about their loyalty. It says '
      + 'only how much of the deep there is.',
  },

  // --- The Exclusives: challenge-only bosses, loosed by the Unmaking, never of the court ---
  maelstromVerge: {
    epithet: 'A drowned tide with no shore to break on.',
    entry:
      'When the Unmaking reached the coast it did not erase the sea — it unmoored '
      + 'it. The Verge is that loosed tide given a will: a heart of undertow that '
      + 'pulls you in, and ranks of water that shove you away, with no leash and no '
      + 'land to anchor either. It was never part of the court. It answers to nothing '
      + 'but its own turning. Read the current, or be read by it.',
  },
  theUnblinking: {
    epithet: 'A watcher that turns motion to stone.',
    entry:
      'Some things the Unmaking did not destroy but woke. The Unblinking had slept '
      + 'beneath Melodia since before the crown, an eye that does not close and does '
      + 'not chase. It does not need to. Under its gaze, movement itself is the '
      + 'trespass — go still and endure the stare, or move and be turned to weeping '
      + 'stone. Everything else you have fought rewards motion. This one damns it.',
  },
  chronophage: {
    epithet: 'A time-eater; your own past returns for you.',
    entry:
      'The stilled clocks of Melodia had to go somewhere. The Chronophage ate '
      + 'them — every stolen hour, every unspent second — and grew a hunger for time '
      + 'still being lived. It rewinds the arena’s flights, sends your own passed '
      + 'moments back along the paths they already flew, makes déjà vu into a '
      + 'weapon. To beat it you must out-run not the boss, but the memory of where '
      + 'you have already been.',
  },
  sirenreef: {
    epithet: 'Everything bright down here is bait.',
    entry: 'The Reef learned the shape of a promise and filled it with teeth. Its steady lights are the places you must leave; its pulsing lights are the places it is pretending to make dangerous. The distinction is fair. It is simply not the distinction Melodia taught you to look for.',
  },
  palimpsest: {
    epithet: 'Your last minute, written over the present.',
    entry: 'The Palimpsest keeps no script of its own. It writes down what the fight has already done, waits until you have learned to dodge it, and lays that page over the one still being written. It is not time reversing. It is memory refusing to stay behind.',
  },

  // --- Act II: The Undersong — what the crown's chord kept asleep beneath
  // Melodia, rising now that the King has let the note go (see ACT2-BIBLE.md).
  gloam: {
    epithet: 'The dark the crown’s light held back.',
    entry:
      'There was no evil in it, once — only the plain dark that is under '
      + 'everything, that the chord lit from above for a thousand years without '
      + 'ever touching. It does not hunt. It simply returns, the way dark returns '
      + 'to a blown-out room, filling whatever the light stops defending. Down '
      + 'here your eyes are a rumor. Carry what little light you have carefully, '
      + 'and learn to strike at where a thing must be rather than where you can '
      + 'see it.',
  },
  antiphon: {
    epithet: 'Two voices that must never meet.',
    entry:
      'In the deep, sound comes in pairs that hate each other — a tone and its '
      + 'shadow, a half-step apart, beating against one another so hard the water '
      + 'shakes. The Antiphon is that quarrel given a body. It sings both at once '
      + 'and dares you to belong to neither. You cannot dodge both voices. You can '
      + 'only choose, moment to moment, which one you are willing to let pass '
      + 'through you.',
  },
  greatHush: {
    epithet: 'The quiet that was here before the first note.',
    entry:
      'Every other thing in Melodia fought you with more — more bullets, more '
      + 'fire, more reach. The Hush fights you with less. Where it spreads, your '
      + 'weapons forget how to speak: the song that has answered for you since '
      + 'the first arena simply stops. It cannot kill you. It can only make you '
      + 'unable to answer while everything else does. Stand where the song still '
      + 'plays, and make your peace with how little floor that leaves.',
  },
  ballast: {
    epithet: 'Everything sinks toward the deep, even the light you throw.',
    entry:
      'It is not a creature so much as a place where down got heavier than it '
      + 'should. Near the Ballast, everything falls the same way — its bullets, '
      + 'your bullets, the arc of your own aim — all curving toward the weight at '
      + 'its heart. You will fire straight and watch your shots pull wide. Learn '
      + 'the bend, or spend the fight missing a thing that is not even moving '
      + 'much.',
  },
  swell: {
    epithet: 'Play softly, or drown in the answer.',
    entry:
      'The deep learns. Whatever you give it, it gives back louder — a swell '
      + 'that rises to meet exactly how hard you struck, so the greedy burst '
      + 'that ended every other fight only teaches this one to hit harder. There '
      + 'is a volume at which it cannot keep up. Finding it means playing against '
      + 'every instinct a hundred fights have drilled into you: not louder. '
      + 'Steadier.',
  },
  undersong: {
    epithet: 'The first silence, learning at last to move.',
    entry:
      'There is no throne at the bottom. There is the thing the chord was '
      + 'written to cover: not a lord, not a monster — the plain fact that '
      + 'Melodia was always a song sung into a dark that did not ask for it. It '
      + 'has no face because it had no maker. As you press it, it stops merely '
      + 'rising and begins, clumsily, terribly, to sing back — un-tuning the '
      + 'arena around you, taking up the dark and the hush and the weight and '
      + 'the two hating voices all at once. It is learning what you are. Do not '
      + 'let it finish the lesson.',
  },

  // --- Act III: The Anthem — what the phrase you hummed climbing out of the
  // Undersong became once other things picked it up (see ACT3-BIBLE.md).
  ostinato: {
    epithet: 'The ground learned your habits.',
    entry:
      'An ostinato is a figure that repeats until it stops being a choice. '
      + 'This one repeats you. It watched how you cross a room — the same arc, '
      + 'the same corner, the same little retreat you have made ten thousand '
      + 'times since the first arena — and it plays it back a breath later, hard '
      + 'enough to kill. There is no trick to it beyond the one thing a hundred '
      + 'fights have made impossible: do not move the way you move.',
  },
  understudy: {
    epithet: 'It has been watching you work.',
    entry:
      'It has no kit of its own. It has been in the wings of every fight you '
      + 'have won, learning the only craft it has ever seen — yours. Whatever '
      + 'you carry, it has carried longer than you think; whatever you upgrade, '
      + 'it upgrades with you, because the thing it is copying is not your '
      + 'weapon but your taste. It cannot beat you at your own game. It can only '
      + 'make you realise, at length, how your game feels from the other side.',
  },
  tithe: {
    epithet: 'Every song is paid for by somebody.',
    entry:
      'The new song did not arrive free. Somewhere in its spreading, a price was '
      + 'set and quietly passed downward, the way prices are. The Tithe is the '
      + 'collector, and it is scrupulous: it will not be struck by anyone who has '
      + 'not given something up. Not blood — you have spent blood all your life '
      + 'and it is cheap. It wants the things you chose. Hand over what you built '
      + 'yourself with, and it will let you swing.',
  },
  manifold: {
    epithet: 'The verse everyone sings slightly differently.',
    entry:
      'A song that is carried gets carried wrong, and the wrongness is the point '
      + '— a phrase reaches a thousand mouths and becomes a thousand phrases, all '
      + 'of them true. The Manifold does that to the singer. It takes the one of '
      + 'you that walked out of the dark and makes it several, each obeying the '
      + 'same hand, none of them safe. You will not be able to protect them all. '
      + 'That is not a flaw in the fight. It is what happens to anything that '
      + 'gets sung by more than one person.',
  },
  verse: {
    epithet: 'Every singer adds a line. Now it is your turn.',
    entry:
      'It is unfinished, and it knows, and it is polite about it. At each turn it '
      + 'stops and offers you the pen: three ways it might grow, all of them bad '
      + 'for you, one of them less so. There is no refusing — refusal is a choice '
      + 'of verse too, and the worst one. You wrote the first line of this song by '
      + 'accident on a stairway in the dark. Everything after is on purpose.',
  },
  anthem: {
    epithet: 'Your song, in every mouth but yours.',
    entry:
      'There is nothing at the end of this but the thing you started, finished by '
      + 'other people. It is enormous and it is beautiful and not one bar of it is '
      + 'yours anymore. It sings your phrasing back at you with a confidence you '
      + 'have never once had. It does not know your name; it does not have to; an '
      + 'anthem is precisely a song that has forgotten it was written. You came up '
      + 'out of the dark humming, and this is what humming costs. Decide, now, '
      + 'whether you are here to take it back or to let it go.',
  },

  // Act III's guard set (data/act3Bosses.js ACT3_MINIBOSSES) — the third and
  // strangest version of the same idea. The King's heralds loved him; the
  // deep's wardens were never given anything to love; these are neither.
  songHeralds: {
    epithet: 'Not guards. Just the parts that caught on first.',
    entry:
      'Three fragments wait on the last stretch — a path with nobody walking it, '
      + 'a shade holding one of your guns, half a stanza that will not stop being '
      + 'half. Nothing put them here. They are simply the bits of the song that '
      + 'travelled fastest, the way a chorus outruns its verses, and they have '
      + 'ended up between you and the rest of it the way a crowd ends up between '
      + 'you and a door. They do not guard the Anthem. They are just the first '
      + 'people to have heard it, and they are very pleased to sing you what they '
      + 'have got.',
  },
};

// Keyed by character id (data/characters.js).
export const CHAMPION_LORE = {
  vess: {
    epithet: 'The wayfarer who keeps time where the world lost the beat.',
    entry:
      'No title, no house, no grievance with the crown — Vess simply kept walking '
      + 'after the world stopped making sense, spirit-lance in hand. What the road '
      + 'taught was cadence: strike, strike, strike, and never let the measure drop. '
      + 'Unbroken, that rhythm outpaces anything the court can conduct. Broken — one '
      + 'clean hit taken — it starts again from silence. Vess has been counting for '
      + 'a long time now, and the Requiem at the end of the count is the only hymn '
      + 'the Unmaking has not learned to sing over.',
  },
  rook: {
    epithet: 'The last wall of a fallen keep, plate by plate.',
    entry:
      'Rook was a garrison once — or the one soldier left of one, which in the end '
      + 'is the same thing. Where others learned to run, Rook learned to be '
      + 'immovable: aegis plates that swallow a blow whole and rebuild in the lull '
      + 'after, over and over, until the attacker runs out of siege before Rook runs '
      + 'out of wall. The scatter-gun is an afterthought. The point of Rook is the '
      + 'Bastion — the moment the ground is struck, the line reforms, and whatever '
      + 'was closing gets thrown back to where it started.',
  },
  iris: {
    epithet: 'A prodigy of edges, sharpest at the edge of dying.',
    entry:
      'Iris was the court’s youngest artificer, gifted enough to be dangerous and '
      + 'fragile enough to prove it. The arc-needle finds the seam in anything — and '
      + 'finds it more surely the worse things are going, as though the body spends '
      + 'what it has left on precision instead of survival. At the very end of the '
      + 'bar, Iris can call Zero Hour and make the world crawl: every needle lethal, '
      + 'every second borrowed. In a dying kingdom some choose to last. Iris chose to '
      + 'matter, and let the lasting take care of itself.',
  },
  thorn: {
    epithet: 'A close fighter ringed in briars, who meets ruin at arm’s length.',
    entry:
      'Thorn does not believe in distance. Where the whole realm screams keep '
      + 'away, Thorn walks in, wreathed in a briar aura that grinds down whatever '
      + 'comes near and bites hardest at nothing-between-us range. It is a reckless '
      + 'faith — that the safest place in a storm is its eye, close enough to the '
      + 'thing that would kill you that it cannot wind up to swing. When the ring '
      + 'opens into full Thornsong, the choice stops being yours: the arena itself '
      + 'drags everything inward to be close.',
  },
  kestrel: {
    epithet: 'A striker who fights by passing through the enemy.',
    entry:
      'Kestrel treats a battlefield the way a bird treats a sky — never still, '
      + 'never where the blow lands. The dash is the weapon; the Nova bloom is only '
      + 'what falls off it. Each strike that connects pays for the next one, so a '
      + 'good read becomes a line of them, each harder than the last. In Skybreaker '
      + 'even that ledger is suspended: no cost, no cooldown, just Kestrel and a '
      + 'series of detonations where a room used to be. Momentum is the only ward '
      + 'that never breaks.',
  },
  aria: {
    epithet: 'The one who kept singing after the song was covered over.',
    entry:
      'Aria was a chorister too small to matter when the Choir split, and so was '
      + 'overlooked by the Unmaking that unwrote the greater voices. What survives '
      + 'in a forgotten voice is only the refrain — the phrase small enough to be '
      + 'remembered, and so the phrase that gets picked up. Down here, where the '
      + 'deep swallows sound and the loudest thing in the arena is whatever answers '
      + 'you, that is worth more than volume. Aria does not sing louder than the '
      + 'Undersong. Aria sings something the dead can carry.',
  },
  cairn: {
    epithet: 'A marker for the dead that refused to be one.',
    entry:
      'They stacked the stones for Cairn at the edge of the unmade world, the way '
      + 'you mark a grave when there is no body left to bury. Cairn declined. '
      + 'Whatever the deep takes from Cairn it takes on loan — the wounds bank up, '
      + 'patient as sediment, and if Cairn is still standing when the debt comes '
      + 'due, the deep pays it all back with interest. Others learned to avoid the '
      + 'blow. Cairn learned to outlast the one who threw it. Down here, where '
      + 'everything old comes back, Cairn intends to be the oldest thing that '
      + 'returns.',
  },
  quill: {
    epithet: 'Wrote it down so it would stop being hers.',
    entry:
      'Quill was a court scribe, which in Melodia meant a person who turned '
      + 'sound into something that outlasted the singer. When the Unmaking came '
      + 'for the archive she did the only thing scribes do: she kept writing, '
      + 'faster, through it. Nothing she inscribed survived. She has not stopped. '
      + 'Where she stands a mark stays behind, and the marks go on speaking after '
      + 'she has walked away, which is either a craft or a compulsion and she has '
      + 'stopped needing to know which.',
  },
  fugue: {
    epithet: 'Answered herself, because no one else would.',
    entry:
      'A fugue is one voice that will not accept being one voice — it states a '
      + 'thing and then argues the thing, in a second register, against itself. '
      + 'Fugue was the last of a duet. When the other half was unmade she did not '
      + 'stop performing the piece; she simply took both parts, and has been '
      + 'switching between them ever since, fast enough that from a distance it '
      + 'still sounds like two people. Up close it is one person refusing an '
      + 'ending.',
  },
  tine: {
    epithet: 'Learned which half of the noise was hers.',
    entry:
      'A tuning fork is not an instrument. It is the thing you hold up before '
      + 'the instrument, to find out what true sounds like — and Tine was one '
      + 'of the people who held them, in the days when Melodia still needed '
      + 'agreeing on. When the Unmaking came it did not come as silence. It '
      + 'came as everything at once, every voice at every pitch, and the '
      + 'choristers who tried to out-sing it were unwritten in the noise. Tine '
      + 'did the other thing. She stopped trying to hear all of it, picked a '
      + 'pitch, and let everything that was not that pitch go through her. It '
      + 'is not courage and she has never claimed it was. It is a decision, and '
      + 'she makes it again every second she is down here.',
  },
};
